import { Box, Chip, CircularProgress, IconButton, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { tokens } from '../../theme';
import { Button } from '../../components/Button';
import CustomTable from '../../components/CustomTable';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import PixIcon from '@mui/icons-material/Pix';
import LayersOutlinedIcon from '@mui/icons-material/Layers';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from '../../hooks/useTranslation';
import useDebounce from '../../hooks/useDebounce';
import { toast } from 'react-toastify';
import { httpGetRedscoreSummary, httpGetRedscoreMovements, httpGetResaleList, httpGetOrderDetails } from './http/redscore';
import ConfigureValuesModal from './ConfigureValuesModal';
import FilterModal from './FilterModal';
import HistoryModal from '../redCoins/HistoryModal';

const EMPTY_FILTERS = {
  companyId: '',
  status: '',
  type: '',
  paymentType: '',
  startDate: '',
  endDate: '',
};

const TIPO_CONFIG = {
  Compra: { textColor: '#1D4ED8' },
  Consumo: { textColor: '#C2410C' },
  Estorno: { textColor: '#BCA8A8' },
};

const STATUS_CONFIG = {
  Pago: { color: '#DCFCE7', textColor: '#15803D' },
  Confirmado: { color: '#DBEAFE', textColor: '#1D4ED8' },
  Cancelado: { color: '#FEE2E2', textColor: '#B91C1C' },
  Expirado: { color: '#F3F4F6', textColor: '#374151' },
  Pendente: { color: '#FEF9C3', textColor: '#854D0E' },
  Aguardando: { color: '#E0E7FF', textColor: '#3730A3' },
};

const PAYMENT_CONFIG = {
  Pix: { color: '#15803D', Icon: PixIcon },
  Cartão: { color: '#1D4ED8', Icon: CreditCardOutlinedIcon },
  Boleto: { color: '#C2410C', Icon: ReceiptOutlinedIcon },
};

const chipSx = (color, textColor) => ({
  backgroundColor: color,
  color: textColor,
  fontWeight: 700,
  fontSize: '11px',
  borderRadius: '6px',
  height: '22px',
});

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const integerFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);
const formatInteger  = (value) => integerFormatter.format(Number(value) || 0);

const SUMMARY_INITIAL = {
  soldByCard: 0,
  soldByBoleto: 0,
  soldByPix: 0,
  tokensPurchased: 0,
  tokensAvailable: 0,
};

const StatCard = ({ label, value, Icon, iconBg, iconColor, loading }) => (
  <Box
    sx={{
      backgroundColor: '#fff',
      borderRadius: '12px',
      border: '1px solid #E0E0E0',
      p: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '10px',
        backgroundColor: iconBg,
        color: iconColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon fontSize="small" />
    </Box>
    <Typography sx={{ fontSize: '13px', color: '#9E9E9E', fontWeight: 500 }}>
      {label}
    </Typography>
    {loading ? (
      <CircularProgress size={20} sx={{ color: iconColor }} />
    ) : (
      <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: '#2C2C2C', lineHeight: 1 }}>
        {value}
      </Typography>
    )}
  </Box>
);

const RedScoreSuperAdmin = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterButtonLoading, setFilterButtonLoading] = useState(false);
  const [resales, setResales] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const debouncedSearch = useDebounce(searchTerm, 400);

  const [summary, setSummary] = useState(SUMMARY_INITIAL);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);
  const [loadingHistoryData, setLoadingHistoryData] = useState(false);

  const handleOpenHistoryModal = async (row) => {
    setSelectedHistoryRow(null);
    setHistoryModalOpen(true);
    setLoadingHistoryData(true);
    try {
      const response = await httpGetOrderDetails(row.id);
      setSelectedHistoryRow(response);
    } catch (err) {
      console.error('Erro ao buscar dados da consulta', err);
      toast.error('Erro ao carregar histórico da consulta.');
      setHistoryModalOpen(false);
    } finally {
      setLoadingHistoryData(false);
    }
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setSelectedHistoryRow(null);
  };

  const fetchSummary = useCallback(async (activeFilters, search) => {
    try {
      setSummaryLoading(true);
      const data = await httpGetRedscoreSummary({
        companyId: activeFilters?.companyId || undefined,
        startDate: activeFilters?.startDate || undefined,
        endDate: activeFilters?.endDate || undefined,
        search: search || undefined,
      });
      setSummary({ ...SUMMARY_INITIAL, ...data });
    } catch (error) {
      console.error(t('messages.errorFetchingData'), error);
      setSummary(SUMMARY_INITIAL);
    } finally {
      setSummaryLoading(false);
    }
  }, [t]);

  const fetchMovements = useCallback(async (activeFilters, search) => {
    try {
      setMovementsLoading(true);
      const data = await httpGetRedscoreMovements({
        companyId: activeFilters?.companyId || undefined,
        startDate: activeFilters?.startDate || undefined,
        endDate: activeFilters?.endDate || undefined,
        status: activeFilters?.status || undefined,
        type: activeFilters?.type || undefined,
        paymentType: activeFilters?.paymentType || undefined,
        search: search || undefined,
      });
      setMovements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(t('messages.errorFetchingData'), error);
      setMovements([]);
    } finally {
      setMovementsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSummary(filters, debouncedSearch);
    fetchMovements(filters, debouncedSearch);
  }, [filters, debouncedSearch, fetchSummary, fetchMovements]);

  const handleOpenFilterModal = async () => {
    if (filterButtonLoading) return;
    if (resales.length > 0) {
      setFilterModalOpen(true);
      return;
    }
    setFilterButtonLoading(true);
    try {
      const list = await httpGetResaleList();
      setResales(list);
      setFilterModalOpen(true);
    } catch {
      toast.error(t('redscoreSuperAdmin.filterModal.errorLoadResales'));
    } finally {
      setFilterButtonLoading(false);
    }
  };

  const handleApplyFilters = (newFilters) => {
    setSearchTerm('');
    setFilters(newFilters);
  };

  const stats = useMemo(
    () => [
      {
        labelKey: 'redscoreSuperAdmin.stats.soldByCard',
        value: formatCurrency(summary.soldByCard),
        Icon: CreditCardOutlinedIcon,
        iconBg: '#DBEAFE',
        iconColor: '#1565C0',
      },
      {
        labelKey: 'redscoreSuperAdmin.stats.soldByBoleto',
        value: formatCurrency(summary.soldByBoleto),
        Icon: ReceiptOutlinedIcon,
        iconBg: '#FFEDD5',
        iconColor: '#E65100',
      },
      {
        labelKey: 'redscoreSuperAdmin.stats.soldByPix',
        value: formatCurrency(summary.soldByPix),
        Icon: PixIcon,
        iconBg: '#DCFCE7',
        iconColor: '#2E7D32',
      },
      {
        labelKey: 'redscoreSuperAdmin.stats.tokensPurchased',
        value: formatInteger(summary.tokensPurchased),
        Icon: LayersOutlinedIcon,
        iconBg: '#F3E8FF',
        iconColor: '#7B1FA2',
      },
      {
        labelKey: 'redscoreSuperAdmin.stats.tokensAvailable',
        value: formatInteger(summary.tokensAvailable),
        Icon: LayersOutlinedIcon,
        iconBg: '#CCFBF1',
        iconColor: '#00695C',
      },
    ].map((s) => ({ ...s, label: t(s.labelKey) })),
    [summary, t]
  );

  const columns = [
    { field: 'revenda', headerName: t('redscoreSuperAdmin.columns.resale') },
    { field: 'dataHora', headerName: t('redscoreSuperAdmin.columns.dateTime') },
    {
      field: 'valor',
      headerName: t('redscoreSuperAdmin.columns.value'),
      render: (value) => (
        <Typography sx={{ fontSize: '14px' }}>
          {value != null ? formatCurrency(value) : '—'}
        </Typography>
      ),
    },
    {
      field: 'consultas',
      headerName: t('redscoreSuperAdmin.columns.consults'),
      render: (value) => (
        <Typography sx={{ fontSize: '14px' }}>{formatInteger(value)}</Typography>
      ),
    },
    {
      field: 'tipo',
      headerName: t('redscoreSuperAdmin.columns.type'),
      render: (value) => {
        const cfg = TIPO_CONFIG[value] ?? { textColor: '#757575' };
        return <Typography sx={{ fontSize: '12px', color: cfg.textColor }}>{value}</Typography>;
      },
    },
    {
      field: 'status',
      headerName: t('redscoreSuperAdmin.columns.status'),
      render: (value) => {
        const cfg = STATUS_CONFIG[value] ?? { color: '#F5F5F5', textColor: '#757575' };
        return <Chip label={value} size="small" sx={chipSx(cfg.color, cfg.textColor)} />;
      },
    },
    {
      field: 'pagamento',
      headerName: t('redscoreSuperAdmin.columns.payment'),
      render: (value, row) => {
        if (row.tipo === 'Consumo') {
          return (
            <Tooltip title={t('redscoreSuperAdmin.columns.viewHistory')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenHistoryModal(row);
                }}
                sx={{ color: colors.greenAccent[500], '&:hover': { color: colors.greenAccent[300] } }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }
        const cfg = PAYMENT_CONFIG[value] ?? { color: '#757575', Icon: null };
        const { Icon, color } = cfg;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color }}>
            {Icon && <Icon sx={{ fontSize: 16 }} />}
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color }}>{value}</Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box m="20px">
      <ConfigureValuesModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
      />
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
        resales={resales}
      />
      <HistoryModal
        open={historyModalOpen}
        handleClose={handleCloseHistoryModal}
        row={selectedHistoryRow}
        loading={loadingHistoryData}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'flex-start' },
          gap: 2,
          mb: '30px',
        }}
      >
        <Box>
          <Typography variant="h2" color={colors.grey[100]} fontWeight="bold" sx={{ m: '0 0 5px 0' }}>
            {t('redscoreSuperAdmin.title')}
          </Typography>
          <Typography variant="h5" color={colors.grey[400]}>
            {t('redscoreSuperAdmin.subtitle')}
          </Typography>
        </Box>
        <Button
          fullWidth={false}
          onClick={() => setConfigModalOpen(true)}
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
          {t('redscoreSuperAdmin.configureValues')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 4 }}>
        <TextField
          placeholder={t('redscoreSuperAdmin.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          InputProps={{
            sx: {
              backgroundColor: '#fff',
              borderRadius: '4px',
              color: colors.grey[400],
              '& fieldset': { borderColor: colors.primary[400] },
              '&:hover fieldset': { borderColor: colors.greenAccent[400] },
              '&.Mui-focused fieldset': { borderColor: colors.greenAccent[400] },
            },
          }}
          sx={{ flex: { xs: '1 1 100%', md: '0 1 400px' } }}
        />

        <Box sx={{ display: 'flex', gap: 2, flex: { xs: '1 1 100%', md: 'none' } }}>
          <Button
            fullWidth={false}
            onClick={() => setSearchTerm('')}
            sx={{ fontWeight: 700, textTransform: 'uppercase', flex: { xs: 1, md: 'none' } }}
          >
            {t('common.clear')}
          </Button>

          <Button
            fullWidth={false}
            onClick={handleOpenFilterModal}
            disabled={filterButtonLoading}
            sx={{
              border: `1px solid ${colors.primary[400]}`,
              px: '1px',
              color: colors.grey[300],
              flex: { xs: 1, md: 'none' },
              '&:hover': { borderColor: colors.greenAccent[400], color: colors.greenAccent[400] },
            }}
          >
            {filterButtonLoading
              ? <CircularProgress size={18} sx={{ color: colors.grey[300] }} />
              : <FilterAltOutlinedIcon />}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 4 }}>
        {stats.map((stat) => (
          <StatCard key={stat.labelKey} {...stat} loading={summaryLoading} />
        ))}
      </Box>

      <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={3}>
        {t('redscoreSuperAdmin.movements')}
      </Typography>

      {movementsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#83D84D' }} />
        </Box>
      ) : (
        <CustomTable
          columns={columns}
          rows={movements}
          emptyMessage={t('redscoreSuperAdmin.noMovementsFound')}
        />
      )}
    </Box>
  );
};

export default RedScoreSuperAdmin;
